import type { IMenuActionParams, MenuItemDef } from "ag-grid-community";
import { useNavigate } from "@tanstack/react-router";

import type { GetContextMenuSection } from "./helpers";
import { colParams } from "../../utils";
import { logger } from "@artis/logger";
import { getEditCurvesIconHtml } from "../../icons";
import { useThemeMode } from "../../../context/theme";
import { GLOBALISED_NON_MANAGER_EDIT_MENU_TOOLTIP } from "../../curve-management/globalisedFolders";
import { useUserSubscriptionTier } from "../../../data/user";

export const useGetMenuItemsEditCurve = (): GetContextMenuSection => {
  const { disabledFeatures } = useUserSubscriptionTier();
  const mode = useThemeMode();
  const navigate = useNavigate();

  const handleEditCurve = (e: IMenuActionParams) => {
    if (disabledFeatures?.editCurve) return;

    const curveId = colParams(e.column)?.productId;
    if (!curveId) {
      console.error("No curveId found");
      return;
    }

    navigate({ to: "/app/curve-management", search: { mode: "edit", edit: curveId } });
  };

  return (ctx) => {
    // Only show for product columns and when exactly one column is selected
    if (!ctx.isProduct || !ctx.columnId || ctx.cannedOrSourced) {
      return [];
    }

    const column = ctx.api.getColumn(ctx.columnId);
    const headerParams = colParams(column);
    const hasWritePermission = headerParams?.permissions?.some((p) => p.permission === "write") ?? false;
    const packageId = headerParams?.packageId;
    const isGlobalisedForNonManager =
      !ctx.isUmiInternalAdmin && packageId != null && ctx.globalisedPackageIds.has(packageId);

    const isDisabled = disabledFeatures?.editCurve || !hasWritePermission || isGlobalisedForNonManager;
    logger.log("Edit Curve", {
      hasWritePermission,
      isDisabled,
      headerParams,
      isGlobalisedForNonManager,
    });

    return [
      {
        name: "Edit Curve",
        disabled: isDisabled,
        tooltip: disabledFeatures?.editCurve
          ? "Please upgrade your subscription to access this feature"
          : !hasWritePermission
            ? "You don't have write permission for this package"
            : isGlobalisedForNonManager
              ? GLOBALISED_NON_MANAGER_EDIT_MENU_TOOLTIP
              : undefined,
        icon: getEditCurvesIconHtml({
          class: `w-7 h-7 relative -left-1 ${mode === "dark" ? "fill-white" : "fill-black"}`,
        }),
        action: handleEditCurve,
      } as MenuItemDef,
    ];
  };
};
